<script lang="ts">
  import type { Snippet } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { X } from '@lucide/svelte';
  import { portal, trapFocus } from './actions';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    open?: boolean;
    title?: string;
    subtitle?: string;
    width?: number;
    onclose?: () => void;
    headerActions?: Snippet;
    children: Snippet;
    footer?: Snippet;
  }
  let { open = $bindable(false), title, subtitle, width = 520, onclose, headerActions, children, footer }: Props = $props();
  function close() {
    open = false;
    onclose?.();
  }
</script>

{#if open}
  <div use:portal class="layer">
    <!-- svelte-ignore a11y_no_static_element_interactions, a11y_click_events_have_key_events -->
    <div class="backdrop" transition:fade={{ duration: 150 }} onclick={close}></div>
    <div
      class="drawer"
      style:--w="{width}px"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabindex="-1"
      use:trapFocus
      onkeydown={(e) => e.key === 'Escape' && (e.stopPropagation(), close())}
      transition:fly={{ x: 40, duration: 220, easing: cubicOut }}
    >
      <header class="head">
        <div class="titles">
          {#if title}<h2>{title}</h2>{/if}
          {#if subtitle}<p>{subtitle}</p>{/if}
        </div>
        <div class="acts">
          {#if headerActions}{@render headerActions()}{/if}
          <button class="x" type="button" aria-label={t('ui.close')} onclick={close}><X size={18} /></button>
        </div>
      </header>
      <div class="body">{@render children()}</div>
      {#if footer}<footer class="foot">{@render footer()}</footer>{/if}
    </div>
  </div>
{/if}

<style>
  .layer { position: fixed; inset: 0; z-index: var(--z-drawer); }
  .backdrop { position: absolute; inset: 0; background: var(--overlay); backdrop-filter: blur(3px); }
  .drawer {
    position: absolute; top: 0; right: 0; bottom: 0;
    width: min(var(--w), 100vw);
    display: flex; flex-direction: column;
    background: var(--bg-elevated);
    border-left: 1px solid var(--border);
    box-shadow: var(--shadow-lg);
    outline: none;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-3); padding: var(--space-4) var(--space-5); border-bottom: 1px solid var(--border); }
  .titles { min-width: 0; display: flex; flex-direction: column; gap: var(--space-0_5); }
  .titles h2 { font-size: var(--text-lg); }
  .titles p { font-size: var(--text-sm); color: var(--text-2); }
  .acts { display: flex; align-items: center; gap: var(--space-1); }
  .x { border: 0; background: transparent; color: var(--text-3); width: 32px; height: 32px; border-radius: var(--radius-xs); display: grid; place-items: center; }
  .x:hover { background: var(--surface-hover); color: var(--text); }
  .x:focus-visible { outline: none; box-shadow: var(--ring); }
  .body { flex: 1; overflow-y: auto; padding: var(--space-5); }
  .foot { display: flex; gap: var(--space-2); justify-content: flex-end; padding: var(--space-3) var(--space-5); border-top: 1px solid var(--border); flex-wrap: wrap; }
</style>
